/**
 * Pure H OS · Phase D · P0-2 · Reports
 *
 * Executive-facing catalogue of recurring rollups. Data lives in the
 * `pureh_reports` MongoDB collection (6 seeded rows). This page replaces
 * the previous Placeholder route at /pureh/reports.
 *
 * Reuses ONLY existing Pure H primitives: Card, SectionHeader,
 * SeverityBadge, TabBar, ListRow, Drawer. No new visual components.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { DocumentActions } from "../components/DocumentActions";
import { REPORTS, Report, ReportCadence } from "../mocks/reportsData";

const CADENCE_LABEL: Record<ReportCadence, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  "on-launch": "On launch",
};

// Cadence → SeverityBadge palette so we reuse the existing chip
// component instead of introducing a new visual.
// weekly = high-cadence → high (red-ish emphasis)
// monthly = medium
// quarterly / on-launch = low
function cadenceSeverity(c: ReportCadence): "high" | "medium" | "low" {
  if (c === "weekly") return "high";
  if (c === "monthly") return "medium";
  return "low";
}

type Tab = "all" | ReportCadence;

export default function Reports() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (tab === "all" ? REPORTS : REPORTS.filter((r) => r.cadence === tab)),
    [tab],
  );
  const current = openId ? REPORTS.find((r) => r.id === openId) ?? null : null;

  const countByCadence = useMemo(() => {
    const c: Record<string, number> = {};
    REPORTS.forEach((r) => { c[r.cadence] = (c[r.cadence] || 0) + 1; });
    return c;
  }, []);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <Card testId="rep-filters">
        <TabBar<Tab>
          testId="rep-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${REPORTS.length}` },
            { key: "weekly",    label: `Weekly ${countByCadence.weekly ?? 0}` },
            { key: "monthly",   label: `Monthly ${countByCadence.monthly ?? 0}` },
            { key: "quarterly", label: `Quarterly ${countByCadence.quarterly ?? 0}` },
            { key: "on-launch", label: `On launch ${countByCadence["on-launch"] ?? 0}` },
          ]}
        />
      </Card>

      <Card padded={false} testId="rep-list">
        <div className="p-5 flex items-start justify-between flex-wrap gap-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <div>
            <SectionHeader
              eyebrow="Reports"
              title={`${filtered.length} report${filtered.length === 1 ? "" : "s"}`}
            />
            <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
              Recurring executive rollups composed from live modules.
            </div>
          </div>
          <DocumentActions docKey="weekly-digest" testId="rep-doc-actions" />
        </div>
        <div className="p-2">
          {filtered.map((r) => (
            <ListRow key={r.id} testId={`rep-${r.id}`}
              onClick={() => setOpenId(r.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>
                    {CADENCE_LABEL[r.cadence]}
                  </span>
                  <span>{r.title}</span>
                </span>
              }
              sublabel={
                <>
                  {r.audience} · last run {r.lastRunRelative} · next {r.nextRunRelative}
                </>
              }
              trailing={<SeverityBadge severity={cadenceSeverity(r.cadence)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="rep-drawer"
              title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader
              eyebrow={`${CADENCE_LABEL[current.cadence]} report`}
              title={current.audience}
            />
            <div className="text-xs -mt-4" style={{ color: "var(--ph-muted)" }}>
              Last run {current.lastRunRelative} · next run {current.nextRunRelative}
            </div>

            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>
              {current.coverage}
            </div>

            {current.body && (
              <div className="text-sm leading-relaxed whitespace-pre-wrap pt-3" style={{ color: "var(--ph-text)", borderTop: "1px solid var(--ph-border-subtle)" }} data-testid={`rep-body-${current.id}`}>
                {current.body}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Cadence</div>
                <div className="mt-2"><SeverityBadge severity={cadenceSeverity(current.cadence)} /></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Report key</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}><code>{current.key}</code></div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Source modules</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {current.sourceModules.map((m) => (
                  <span key={m} className="px-2 py-1 text-xs rounded"
                    style={{
                      background: "var(--ph-surface-well)",
                      border: "1px solid var(--ph-border-subtle)",
                      color: "var(--ph-text)",
                    }}>
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
